import moment from "moment-timezone";
function getUTCDateWithLocalTimezone(m) {
  const utc = m.utc();
  return new Date(utc.year(), utc.month(), utc.date());
}
function getTodayAsDate() {
  return getUTCDateWithLocalTimezone(moment());
}
function formatDayOfWeek(date, format) {
  return date ? moment(date).utcOffset(-date.getTimezoneOffset()).format(format) : "";
}
function formatDate(date, format, isLocal = true, withTimeZone = true) {
  let result = null;
  if (date) {
    result = formatLocalMomentDate(moment(date).local(isLocal), format, isLocal);
    result = withTimeZone ? `${result} ${moment().zoneAbbr()}` : result;
  }
  return result;
}
function formatUtcDate(date, format, withTimeZone = true) {
  let result = null;
  if (date) {
    result = formatMomentDate(moment.utc(date), format);
    result = withTimeZone ? `${result} ${moment().zoneAbbr()}` : result;
  }
  return result;
}
function formatLocalMomentDate(date, format, isLocal = true) {
  return formatMomentDate(date.local(isLocal), format);
}
function formatMomentDate(date, format) {
  let result = null;
  switch (format) {
    case "date":
      result = date.format("ll");
      break;
    case "time":
      result = date.format("LTS");
      break;
    case "date-time-with-seconds":
      result = date.format("MMM DD, YYYY h:mm:ss A");
      break;
    case "iso-date":
      result = date.format("YYYY-MM-DD");
      break;
    case "date-time":
    default:
      result = date.format("lll");
      break;
  }
  return result;
}
function formatDateRange(fromLocal, toLocal) {
  let res = "";
  const from = fromLocal ? getStartOfDate(fromLocal) : null;
  const to = toLocal ? getEndOfDate(toLocal) : null;
  const fromFormatted = from ? formatDate(from, "date", false) : null;
  const toFormatted = to ? formatDate(to, "date", false) : null;
  if (fromFormatted && toFormatted) {
    if (fromFormatted === toFormatted) {
      res = `${fromFormatted}`;
    } else {
      res = `${fromFormatted} - ${toFormatted}`;
    }
  } else if (toFormatted) {
    res = `${toFormatted}`;
  } else if (from) {
    res = `${fromFormatted}`;
  }
  return res;
}
const getStartOfDate = (dateLocal) => {
  const localStartDate = getLocalDate(dateLocal)?.startOf("day");
  const res = getUtcDate(localStartDate);
  return res;
};
const getEndOfDate = (dateLocal) => {
  const localEndDate = getLocalDate(dateLocal)?.endOf("day");
  return getUtcDate(localEndDate);
};
const getUtcDate = (date) => {
  return date ? moment.utc(date) : null;
};
const getLocalDate = (date) => {
  return date ? moment(date.toDateString()).local() : null;
};
function getNHoursAgoAsSeconds(hours) {
  return moment().subtract(Number(`${hours}`), "h").unix();
}
const endDateNow = "now";
function totalHoursPassedFromNow(date) {
  if (date === endDateNow) return "0";
  return date ? `${moment().diff(moment.unix(Number(date)), "hours")}` : "";
}
function timestampSecondsToMoment(seconds) {
  return seconds !== null && seconds !== void 0 ? moment.unix(Number(`${seconds}`)) : null;
}
function timestampSecondsToDate(seconds) {
  return timestampSecondsToMoment(seconds)?.toDate();
}
function getLocalDateWithoutTimezone(m) {
  return m.local().utcOffset(0, true).toDate();
}
function isCurrentDate(date) {
  const endOfToday = moment.utc().endOf("day");
  const endOfDate = moment(getLocalDateWithoutTimezone(moment(date))).endOf("day");
  return endOfToday.isSame(endOfDate, "day");
}
function momentToTimestampSeconds(date) {
  return date?.unix();
}
const sortByTimestamp = (first, second) => {
  let result;
  if (!first?.seconds) {
    result = -1;
  } else if (!second?.seconds) {
    result = 1;
  } else {
    result = first.seconds - second.seconds;
  }
  return result;
};
function getUTCTimestamp() {
  return { seconds: moment.utc().unix() };
}
function secondsSinceEpoch(date) {
  return Math.floor(date.getTime() / 1e3);
}
function isRecent(date, minutes) {
  return date ? moment().diff(moment.unix(date.seconds), "minutes") < minutes : false;
}
function isDiffNHours(from, to, nHours) {
  return Math.abs(moment.unix(from).diff(moment.unix(to), "hours")) === nHours;
}
function formatElapsedTime(minutes) {
  return moment.duration(minutes, "minutes").humanize();
}
function formatTimestamp(timestamp, format, withTimeZone = true) {
  let result = null;
  const seconds = timestamp?.seconds;
  if (seconds) {
    const formattedDate = formatLocalMomentDate(moment.unix(seconds).local(true), format);
    result = withTimeZone ? `${formattedDate} ${moment().zoneAbbr()}` : formattedDate;
  }
  return result;
}
function getFormatTemplate(format) {
  let result = null;
  const localeData = moment.localeData();
  switch (format) {
    case "date":
      result = localeData.longDateFormat("ll");
      break;
    case "time":
      result = localeData.longDateFormat("LTS");
      break;
    case "date-time":
    default:
      result = localeData.longDateFormat("lll");
      break;
  }
  return result;
}
const sortByDate = (first, second) => {
  let result;
  if (!first) {
    result = -1;
  } else if (!second) {
    result = 1;
  } else {
    result = first.getTime() - second.getTime();
  }
  return result;
};
const addDaysToDate = (date, days) => {
  const result = new Date(date);
  const newDate = date.getDate() + days;
  result.setDate(newDate);
  return result;
};
const setMomentRelativeThresholds = () => {
  moment.relativeTimeThreshold("s", 60);
  moment.relativeTimeThreshold("m", 60);
  moment.relativeTimeThreshold("h", 24);
  moment.relativeTimeThreshold("d", 30);
  moment.relativeTimeThreshold("M", 12);
  moment.relativeTimeThreshold("ss", 0);
};
const convertDaysToSeconds = (days) => {
  return 24 * 60 * 60 * days;
};
const convertSecondsToDays = (seconds) => {
  return Math.ceil(seconds / 24 / 60 / 60);
};
function isFutureDate(date) {
  return moment(date).diff(moment()) > 0;
}
function isValidDataRangeFilter(filter, prefix, validIfNotExist) {
  const fromValue = filter?.find((f) => f.key === `${prefix}.from`)?.value;
  const toValue = filter?.find((f) => f.key === `${prefix}.to`)?.value;
  return fromValue ? Number(fromValue) && Number(toValue) ? fromValue <= toValue : true : !!validIfNotExist;
}
function getLastNDaysDateRange(rangePeriodDays) {
  const now = /* @__PURE__ */ new Date();
  const startDate = getUtcDate(addDaysToDate(now, -rangePeriodDays))?.startOf("day");
  const startDateSeconds = momentToTimestampSeconds(startDate);
  return [startDateSeconds, endDateNow];
}
function getTodayDateRange() {
  return getLastNDaysDateRange(0);
}
function getLastWeekDateRange() {
  let endOfWeek;
  const now = /* @__PURE__ */ new Date();
  const dayOfWeek = getUtcDate(now).day();
  const isSunday = dayOfWeek === 0;
  if (!isSunday) {
    endOfWeek = addDaysToDate(now, -dayOfWeek);
  } else {
    endOfWeek = now;
  }
  const startDate = getUtcDate(addDaysToDate(endOfWeek, -6))?.startOf("day");
  const startDateSeconds = momentToTimestampSeconds(startDate);
  const endDate = getUtcDate(endOfWeek)?.endOf("day");
  const endDateSeconds = momentToTimestampSeconds(endDate);
  return [startDateSeconds, endDateSeconds];
}
function getStartOfWeek(subtractedWeeks = 0) {
  const startWeek = moment().utc().subtract(moment.duration(subtractedWeeks, "weeks"));
  const dayOfWeek = startWeek.isoWeekday();
  return startWeek.add(dayOfWeek >= 1 ? -(dayOfWeek - 1) : -6, "day").startOf("day");
}
function getEndOfWeek(subtractedWeeks = 0) {
  const startOfWeek = getStartOfWeek(subtractedWeeks);
  return startOfWeek.add(6, "day").endOf("day");
}
function isRangeInSameWeek(from, to) {
  const fromDateInMoment = timestampSecondsToMoment(from)?.utc();
  const toDateInMoment = to !== endDateNow ? timestampSecondsToMoment(to)?.utc() : moment()?.utc();
  return Boolean(fromDateInMoment) && Boolean(toDateInMoment) && fromDateInMoment.isSame(toDateInMoment, "week");
}
function getYearDateRange(year) {
  return {
    from: /* @__PURE__ */ new Date(`${year}-01-01`),
    to: /* @__PURE__ */ new Date(`${year}-12-31`)
  };
}
function parseDateIntoUTC(date, inputMask) {
  return moment.utc(date, inputMask).toDate();
}
function checkDateStringSatisfiesMask(date, inputMask) {
  return moment(date, inputMask, true).isValid();
}
function getEndOfFutureYear(addedYears = 1) {
  return getUTCDateWithLocalTimezone(moment().add(addedYears, "year").endOf("year"));
}
function getPreviousYear() {
  return moment.utc().year() - 1;
}
function getStartOfPreviousYear() {
  return moment.utc().subtract(1, "year").startOf("year");
}
export {
  addDaysToDate,
  checkDateStringSatisfiesMask,
  convertDaysToSeconds,
  convertSecondsToDays,
  endDateNow,
  formatDate,
  formatDateRange,
  formatDayOfWeek,
  formatElapsedTime,
  formatMomentDate,
  formatTimestamp,
  formatUtcDate,
  getEndOfDate,
  getEndOfFutureYear,
  getEndOfWeek,
  getFormatTemplate,
  getLastNDaysDateRange,
  getLastWeekDateRange,
  getLocalDateWithoutTimezone,
  getNHoursAgoAsSeconds,
  getPreviousYear,
  getStartOfDate,
  getStartOfPreviousYear,
  getStartOfWeek,
  getTodayAsDate,
  getTodayDateRange,
  getUTCDateWithLocalTimezone,
  getUTCTimestamp,
  getUtcDate,
  getYearDateRange,
  isCurrentDate,
  isDiffNHours,
  isFutureDate,
  isRangeInSameWeek,
  isRecent,
  isValidDataRangeFilter,
  momentToTimestampSeconds,
  parseDateIntoUTC,
  secondsSinceEpoch,
  setMomentRelativeThresholds,
  sortByDate,
  sortByTimestamp,
  timestampSecondsToDate,
  timestampSecondsToMoment,
  totalHoursPassedFromNow
};
//# sourceMappingURL=date.js.map
